import React, { useCallback } from "react";
import { Formik, FormikProps, Form, FormikHelpers } from "formik";
import { Grid, Button, styled, alpha } from "@mui/material";
// Formik Fields
import { FormikTextField } from "../../../FormikComponents/FormikWrappers";
import { IPairing, IPairingNote } from "@vmosolutions/oc-interfaces";
//Yup Validation
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IEmbeddedFormState } from "../PairingEditorWindow";

interface IPairingNotesFormProps {
  updatePairingNotes: (pairingNotes: IPairingNote[]) => void;
  setEmbeddedFormStates: React.Dispatch<React.SetStateAction<IEmbeddedFormState>>;
  initPairing: IPairing;
}

// New Note
export const initialValues: IPairingNote = {
  note: undefined,
  createdAt: undefined,
  createdBy: undefined,
};

export type IPairingNoteForm = FormikProps<IPairingNote>;

const OCPairingNoteForm = styled("div")(({ theme }) => ({
  "& .formik_field_container": {
    "& .formik_field": {
      maxWidth: 350,
    },
    "& .pairing_note_textfield": {
      "& .input_control label": { width: 180 },
    },
    "& p.required_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
    },
    "& .form_actions": {
      margin: 10,
      "& button": {
        margin: "0px 2px",
        fontSize: 11,
        lineHeight: "12px",
      },
    },
  },
  "& .event_dropzone": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flexGrow: 1,
    border: `1px dashed ${theme.custom.forms.labels}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    color: theme.palette.primary.contrastText,
    borderRadius: 3,
    padding: 20,
    marginBottom: "10px",
    "& .title": { fontSize: 18, fontWeight: "bold" },
    "& .body": { fontSize: 14, fontWeight: 300, textAlign: "center" },
  },
}));

// NoteSchema Form Validation with YUP
const NoteSchema = Yup.object().shape({
  note: Yup.string().required("Note is required"), //TODO Look into standardizing regex validation for notes.
});

export const PairingNoteForm = (props: IPairingNotesFormProps): JSX.Element => {
  const { updatePairingNotes, setEmbeddedFormStates, initPairing } = props;
  const authentication = useSelector((state: IRootState) => state.authentication);
  const userId = authentication?.attributes?._id ?? undefined;
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[initPairing?._id]);

  const handleSubmit = useCallback(
    (note: IPairingNote, helpers: FormikHelpers<IPairingNote>): void => {
      const newNote = { ...note, ...{ createdAt: new Date()?.valueOf(), createdBy: userId } };
      const newPairingNotes = [...storedPairing.pairingNotes, newNote];
      updatePairingNotes(newPairingNotes);
      helpers.resetForm(); // Reset form on submit.
    },
    [storedPairing, userId]
  );

  return (
    <OCPairingNoteForm>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={NoteSchema}
        validateOnBlur={true}
      >
        {(formProps: IPairingNoteForm) => {
          return (
            <>
              <div style={{ display: "flex", margin: "10px 5px" }}>Add Pairing Note</div>
              <Form>
                <Grid container className="formik_field_container" direction="row" wrap="wrap">
                  <FormikTextField
                    className="pairing_note_textfield"
                    label="note"
                    name="note"
                    multiline={true}
                    minRows={2}
                    maxRows={5}
                  />

                  <div className="form_actions" style={{ marginLeft: 200 }}>
                    <Button type="reset" variant="contained" color="secondary" disabled={!formProps.dirty}>
                      Clear
                    </Button>
                    <Button type="submit" variant="contained" color="primary" disabled={!formProps.dirty}>
                      Add
                    </Button>
                  </div>
                </Grid>
              </Form>
            </>
          );
        }}
      </Formik>
    </OCPairingNoteForm>
  );
};
